import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { chatApi } from '@/api/chat'
import type { ChatAttachment, ChatMessage } from '@/api/chat'
import { useChatStore } from '@/stores/chatStore'
import { extractDataTransferFiles } from '@/utils/clipboard'
import { unlockChatSound } from '@/utils/chatSound'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

const IMAGE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
const FILE_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
/** Зеркало правила `attachments => array|max:10` backend (SendMessageRequest). */
const MAX_PENDING_ATTACHMENTS = 10

/**
 * Белый список типов вложений — зеркало `config/chat.php` на бэкенде.
 *
 * Расширения, а не mime: браузеры проставляют File.type неровно (у .docx и
 * .zip он часто пуст или application/octet-stream), а сервер валидирует
 * именно по расширению.
 */
const ACCEPTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const
const ACCEPTED_DOCUMENT_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip',
] as const
const ACCEPTED_EXTENSIONS: readonly string[] = [
  ...ACCEPTED_IMAGE_EXTENSIONS,
  ...ACCEPTED_DOCUMENT_EXTENSIONS,
]

/** Значение атрибута accept скрытого input — тот же список. */
export const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  ...ACCEPTED_DOCUMENT_EXTENSIONS.map((extension) => `.${extension}`),
].join(',')

const fileExtension = (file: File): string => {
  const dot = file.name.lastIndexOf('.')

  return dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase()
}

/**
 * Клиент отбивает только то, в чём уверен.
 *
 * Сервер валидирует правилом `mimes:`, то есть по угаданному из содержимого
 * типу, а не по имени файла. Поэтому файл без расширения (перетаскивание из
 * вкладки браузера, файлы без суффикса) пропускаем к серверу — иначе клиент
 * запрещал бы то, что сервер принимает. Заявленное чужое расширение — .exe,
 * .iso, .mp4 — отбиваем здесь: ради этого проверка и добавлена.
 */
const isAcceptedFile = (file: File): boolean => {
  const extension = fileExtension(file)

  if (ACCEPTED_EXTENSIONS.includes(extension)) return true

  return extension === ''
}

export interface ChatComposerDragProps {
  onDragEnter: (event: React.DragEvent) => void
  onDragLeave: (event: React.DragEvent) => void
  onDragOver: (event: React.DragEvent) => void
  onDrop: (event: React.DragEvent) => void
}

export interface ChatComposerController {
  roomId: number | null
  canWrite: boolean
  text: string
  setText: (value: string) => void
  pendingAttachments: ChatAttachment[]
  removeAttachment: (path: string) => void
  editingId: number | null
  startEdit: (message: ChatMessage) => void
  cancelEdit: () => void
  isUploading: boolean
  isDragOver: boolean
  dragProps: ChatComposerDragProps
  textInputRef: React.MutableRefObject<HTMLTextAreaElement | null>
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>
  handleFilePick: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handlePaste: (event: React.ClipboardEvent) => void
  insertEmoji: (emoji: string) => void
  send: () => Promise<void>
  sendSticker: (stickerId: number) => Promise<void>
}

/**
 * Логика композера одной комнаты: текст, вложения, правка, стикеры,
 * drag-and-drop и вставка из буфера.
 *
 * Хук, а не компонент: режим правки начинается из ленты (контекстное меню
 * пузыря), поэтому состояние композера должно быть видно и ленте, и самому
 * композеру. Страница чата и панель чата урока используют один и тот же хук.
 */
export function useChatComposer(
  roomId: number | null,
  canWrite: boolean,
  onError: (message: string | null) => void
): ChatComposerController {
  const { t } = useTranslation()
  const sendMessage = useChatStore((s) => s.sendMessage)
  const editMessage = useChatStore((s) => s.editMessage)

  const [text, setText] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [uploadingRoomIds, setUploadingRoomIds] = useState<number[]>([])
  const [isDragOver, setIsDragOver] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const textInputRef = useRef<HTMLTextAreaElement | null>(null)
  const dragDepthRef = useRef(0)

  /**
   * Комнаты, в которые прямо сейчас идёт загрузка вложений. Ref ставится
   * синхронно — state обновится только к следующему рендеру, а вторую пачку в
   * ту же комнату надо отсечь сразу. Учёт покомнатный: загрузка в одном чате
   * не должна мешать прикреплять файлы и писать в других.
   */
  const uploadingRoomIdsRef = useRef<Set<number>>(new Set())
  const setUploadingRoom = (id: number, uploading: boolean) => {
    if (uploading) uploadingRoomIdsRef.current.add(id)
    else uploadingRoomIdsRef.current.delete(id)
    setUploadingRoomIds([...uploadingRoomIdsRef.current])
  }

  /**
   * Поколение композера. Смена комнаты, переход в режим правки и удачная
   * отправка обнуляют отложенные вложения — догрузившийся после этого файл не
   * должен приземлиться в уже очищенный композер (тем более чужой комнаты).
   */
  const composerGenerationRef = useRef(0)
  const resetComposerGeneration = () => {
    composerGenerationRef.current += 1
  }

  /**
   * Композер принадлежит комнате. Комнату меняют не только клики по списку, но
   * и «Новый чат», и deep-link'и — поэтому очистка висит на самом `roomId`, а
   * не на обработчике клика. Заодно растёт поколение композера: догружающаяся
   * пачка вложений не должна приземлиться в чат, который уже сменили.
   */
  useEffect(() => {
    resetComposerGeneration()
    setEditingId(null)
    setText('')
    setPendingAttachments([])
  }, [roomId])

  useEffect(() => {
    dragDepthRef.current = 0
    setIsDragOver(false)
  }, [roomId, canWrite])

  /** Загрузка идёт именно в эту комнату — только она гасит здешние кнопки. */
  const isUploading = roomId !== null && uploadingRoomIds.includes(roomId)

  /**
   * Единственный путь загрузки вложений — меню «Файл», drag-and-drop и вставка
   * из буфера.
   *
   * В одной комнате пачка одна: `uploadingRoomIdsRef` ставится синхронно, поэтому
   * вставка поверх незавершённого дропа не запускает второй проход. Без этого
   * два параллельных вызова считали бы свободные слоты каждый по своему
   * рендеру и вместе перевалили бы за лимит. Пачки в разных комнатах при этом
   * идут независимо: композер очищается на смене комнаты, а остаток «чужой»
   * пачки отсекает поколение.
   *
   * Свободные слоты внутри пачки считаются локально: setPendingAttachments не
   * обновляет замыкание, а лишние файлы нужно отсечь до отправки на сервер —
   * иначе send упрётся в лимит уже с мусором в хранилище.
   */
  const uploadFiles = async (files: File[]) => {
    if (roomId === null || !canWrite || files.length === 0) return
    if (uploadingRoomIdsRef.current.has(roomId)) {
      onError(t('chat.uploadInProgress'))
      return
    }

    let freeSlots = MAX_PENDING_ATTACHMENTS - pendingAttachments.length
    if (freeSlots <= 0) {
      onError(t('chat.attachmentsLimit', { max: MAX_PENDING_ATTACHMENTS }))
      return
    }

    unlockChatSound()
    onError(null)
    const generation = composerGenerationRef.current
    setUploadingRoom(roomId, true)
    try {
      for (const file of files) {
        if (freeSlots === 0) {
          onError(t('chat.attachmentsLimit', { max: MAX_PENDING_ATTACHMENTS }))
          break
        }
        // Тип проверяем до отправки: accept висит только на скрытом input, а
        // перетаскивание и вставка из буфера идут сюда же. Без проверки .exe
        // или .iso целиком уезжали на сервер и возвращались 422 без внятного
        // объяснения, какой файл не подошёл.
        if (!isAcceptedFile(file)) {
          onError(t('chat.fileTypeNotSupported', { name: file.name }))
          continue
        }

        const maxSize = file.type.startsWith('image/')
          ? IMAGE_MAX_FILE_SIZE_BYTES
          : FILE_MAX_FILE_SIZE_BYTES
        if (file.size > maxSize) {
          onError(
            file.type.startsWith('image/')
              ? t('chat.imageTooLarge')
              : t('chat.fileTooLarge')
          )
          continue
        }
        // Ошибка одного файла не должна ронять остальную пачку: показываем её
        // и продолжаем, иначе после 422 на третьем файле молча пропадали бы
        // все следующие.
        try {
          const attachment = await chatApi.uploadFile(roomId, file)
          // Пока файл летел на сервер, композер могли очистить: уйти в другую
          // комнату, начать правку сообщения или отправить его. Дописывать в
          // него остаток пачки нельзя — вложения оказались бы в чужом чате или
          // в чужом сообщении.
          if (composerGenerationRef.current !== generation) break
          setPendingAttachments((prev) => [...prev, attachment])
          freeSlots -= 1
        } catch (e) {
          // Таймаут и обрыв сети дают технический текст axios — показываем
          // вместо него понятную строку, как и на остальных экранах.
          onError(displayErrorMessage(getErrorMessage(e), t('chat.uploadFailed')))
        }
      }
    } finally {
      setUploadingRoom(roomId, false)
    }
  }

  /**
   * Свежая ссылка на загрузчик для мемоизированных drag-обработчиков: сам
   * `uploadFiles` замыкается на `pendingAttachments` и считает свободные
   * слоты, поэтому в мемо его класть нельзя — счёт был бы от старого рендера.
   */
  const uploadFilesRef = useRef(uploadFiles)
  uploadFilesRef.current = uploadFiles

  const handleFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    event.target.value = ''
    if (!fileList?.length) return
    await uploadFiles(Array.from(fileList))
  }

  // Ссылка стабильна между рендерами: объект спредится в контейнер ленты, и
  // пересоздание на каждый символ в поле ввода меняло бы там все обработчики.
  const dragProps: ChatComposerDragProps = useMemo(() => ({
    onDragEnter: (event) => {
      event.preventDefault()
      event.stopPropagation()
      if (!canWrite || roomId === null) return
      if (!event.dataTransfer.types.includes('Files')) return
      dragDepthRef.current += 1
      setIsDragOver(true)
    },
    onDragLeave: (event) => {
      event.preventDefault()
      event.stopPropagation()
      dragDepthRef.current -= 1
      if (dragDepthRef.current <= 0) {
        dragDepthRef.current = 0
        setIsDragOver(false)
      }
    },
    onDragOver: (event) => {
      event.preventDefault()
      event.stopPropagation()
      if (canWrite && roomId !== null) {
        event.dataTransfer.dropEffect = 'copy'
      }
    },
    onDrop: (event) => {
      event.preventDefault()
      event.stopPropagation()
      dragDepthRef.current = 0
      setIsDragOver(false)
      if (!canWrite || roomId === null) return
      void uploadFilesRef.current(extractDataTransferFiles(event.dataTransfer))
    },
  }), [canWrite, roomId])

  const send = async () => {
    if (roomId === null || !canWrite) return
    // Отправка посреди загрузки увела бы сообщение с половиной вложений, а
    // догрузившиеся файлы остались бы висеть в композере уже после отправки.
    // Блокируется только комната, куда идёт загрузка: в остальных чатах писать
    // можно как обычно.
    if (uploadingRoomIdsRef.current.has(roomId)) {
      onError(t('chat.uploadInProgress'))
      return
    }
    const trimmed = text.trim()
    if (!trimmed && pendingAttachments.length === 0) return

    const payload = {
      text: trimmed || null,
      // pendingAttachments — только image/file (стикеры отправляются сразу из
      // picker); фильтр вместо каста — случайный сторонний тип не улетит
      // на сервер с полями image-вложения.
      attachments: pendingAttachments
        .filter((a): a is ChatAttachment & { type: 'image' | 'file' } =>
          a.type === 'image' || a.type === 'file'
        )
        .map((a) => ({
          type: a.type,
          path: a.path,
          name: a.name,
          mime: a.mime ?? 'application/octet-stream',
          size: a.size ?? 0,
        })),
    }

    try {
      if (editingId) {
        await editMessage(roomId, editingId, payload)
        setEditingId(null)
      } else {
        await sendMessage(roomId, payload)
      }
      resetComposerGeneration()
      setText('')
      setPendingAttachments([])
    } catch (e) {
      onError(getErrorMessage(e))
    }
  }

  const sendSticker = async (stickerId: number) => {
    if (roomId === null || !canWrite) return
    unlockChatSound()
    onError(null)
    try {
      await sendMessage(roomId, {
        attachments: [{ type: 'sticker', sticker_id: stickerId }],
      })
    } catch (e) {
      onError(getErrorMessage(e))
    }
  }

  /**
   * Вставка emoji в позицию курсора (или в конец) с возвратом фокуса и
   * каретки. Каретка и текст читаются синхронно (state updater обязан быть
   * чистым — StrictMode вызывает его дважды), фокус — после коммита кадра.
   */
  const insertEmoji = useCallback((emoji: string) => {
    const input = textInputRef.current
    setText((prev) => {
      const start = input?.selectionStart ?? prev.length
      const end = input?.selectionEnd ?? prev.length
      return prev.slice(0, start) + emoji + prev.slice(end)
    })
    const caret = (input?.selectionStart ?? 0) + emoji.length
    requestAnimationFrame(() => {
      input?.focus()
      input?.setSelectionRange(caret, caret)
    })
  }, [])

  /**
   * Ctrl+V в композере: скриншот и «Копировать изображение» идут вложением тем
   * же путём, что и drag-and-drop.
   *
   * Форматированный фрагмент отдаём браузеру целиком. Excel, Word и Outlook
   * кладут в буфер сразу текст, HTML и PNG-рендер выделения: перехватив такую
   * вставку, мы вместо текста молча прикрепили бы картинку с данными, которые
   * пользователь показывать не собирался. Признак такого буфера — текст плюс
   * `text/html`; копия файла из проводника (текст = имя файла, HTML нет) и
   * скриншот (текста нет вовсе) остаются вложением, и `preventDefault` не даёт
   * имени файла упасть в поле ввода.
   */
  const handlePaste = (event: React.ClipboardEvent) => {
    if (!canWrite || roomId === null) return
    const data = event.clipboardData
    const files = extractDataTransferFiles(data)
    if (files.length === 0) return
    const isFormattedFragment =
      Boolean(data.getData('text/plain').trim()) && data.types.includes('text/html')
    if (isFormattedFragment) return
    event.preventDefault()
    void uploadFiles(files)
  }

  const startEdit = useCallback((message: ChatMessage) => {
    resetComposerGeneration()
    setEditingId(message.id)
    setText(message.text ?? '')
    setPendingAttachments([])
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingId(null)
    setText('')
  }, [])

  const removeAttachment = useCallback((path: string) => {
    setPendingAttachments((prev) => prev.filter((x) => x.path !== path))
  }, [])

  return {
    roomId,
    canWrite,
    text,
    setText,
    pendingAttachments,
    removeAttachment,
    editingId,
    startEdit,
    cancelEdit,
    isUploading,
    isDragOver,
    dragProps,
    textInputRef,
    fileInputRef,
    handleFilePick,
    handlePaste,
    insertEmoji,
    send,
    sendSticker,
  }
}
